import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';
import apiService from '../services/api';
import SupportDeskTicketFormDialog from './SupportDeskTicketFormDialog';

interface Props {
  accountId: string;
}

const AccountSupportDeskTab: React.FC<Props> = ({ accountId }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getSupportDeskTickets({
        account_id: accountId,
        page: 1,
        per_page: 100,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      setRows(res.tickets || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="primary.main">Support desk</Typography>
        <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setFormOpen(true)}>
          New ticket
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={32} /></Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No support desk tickets for this account.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.ticket_number}</TableCell>
                  <TableCell>{t.subject}</TableCell>
                  <TableCell><Chip label={t.status} size="small" /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => navigate(`/crm/support-desk/${t.id}`)} title="View">
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <SupportDeskTicketFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => { setFormOpen(false); load(); }}
        accountId={accountId}
      />
    </Paper>
  );
};

export default AccountSupportDeskTab;
